import { useRef, type ReactNode } from "react";

/**
 * One plain textarea with a line-number gutter that scrolls with it.
 *
 * `dir="auto"` plus `unicode-bidi: plaintext` makes each line (paragraph) pick
 * its own direction from its first strong character, so Hebrew lines read
 * right-to-left. Browser limit: bidi is decided per paragraph, i.e. per line,
 * never per word inside a line.
 */
export function FileEditor({
  value,
  onChange,
  onSave,
  readOnly,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  readOnly: boolean;
  label: ReactNode;
}) {
  const gutter = useRef<HTMLDivElement>(null);
  const lineCount = value.split("\n").length;
  return (
    <div className="flex h-[28rem] overflow-hidden rounded-md border border-border bg-card font-mono text-sm">
      <div
        ref={gutter}
        aria-hidden="true"
        dir="ltr"
        className="select-none overflow-hidden border-r border-border bg-secondary px-2 py-2 text-right leading-5 text-muted-foreground"
      >
        {Array.from({ length: lineCount }, (_, index) => (
          <div key={index}>{index + 1}</div>
        ))}
      </div>
      <textarea
        aria-label={typeof label === "string" ? label : "File content"}
        value={value}
        readOnly={readOnly}
        wrap="off"
        spellCheck={false}
        dir="auto"
        style={{ unicodeBidi: "plaintext" }}
        className="min-w-0 flex-1 resize-none overflow-auto whitespace-pre bg-transparent px-2 py-2 leading-5 outline-none"
        onChange={(event) => onChange(event.target.value)}
        onScroll={(event) => {
          if (gutter.current) {
            gutter.current.scrollTop = event.currentTarget.scrollTop;
          }
        }}
        onKeyDown={(event) => {
          if (
            (event.ctrlKey || event.metaKey) &&
            event.key.toLowerCase() === "s"
          ) {
            event.preventDefault();
            onSave();
          }
        }}
      />
    </div>
  );
}
